/**
 * 场景化新建文档预设（DESIGN.md §2.6 补充）。
 *
 * 纯数据 + 类型：不属于文档模型本身（`document/draft.ts` 的 `createDocument`
 * 不需要知道这些预设的存在），也不是 UI 展示细节——`NewDocumentDialog.tsx`
 * 和 `EmptyCanvasOverlay.tsx` 都会用到同一份数据，放在应用层供两处复用。
 */

export interface DocumentTemplate {
  readonly id: string;
  readonly label: string;
  /** 像素，权威单位——跟 `NewDocumentInfo.width/height` 同一基准。 */
  readonly width: number;
  readonly height: number;
  /** 展示在预设卡片副标题的简短说明，例如宽高比。 */
  readonly note: string;
}

export interface TemplateCategory {
  readonly id: string;
  readonly label: string;
  readonly templates: readonly DocumentTemplate[];
}

export const DOCUMENT_TEMPLATE_CATEGORIES: readonly TemplateCategory[] = [
  {
    id: 'social',
    label: '社交媒体',
    templates: [
      { id: 'ig-post', label: 'Instagram 正方形帖子', width: 1080, height: 1080, note: '1:1' },
      { id: 'ig-story', label: 'Instagram/Reels 故事', width: 1080, height: 1920, note: '9:16' },
      { id: 'og-share', label: 'Facebook/X 分享横图', width: 1200, height: 630, note: '1.91:1' },
      { id: 'wechat-cover', label: '微信公众号首图', width: 900, height: 500, note: '2.35:1 建议区' },
    ],
  },
  {
    id: 'ecommerce',
    label: '电商',
    templates: [
      { id: 'taobao-main', label: '淘宝/天猫主图', width: 800, height: 800, note: '1:1' },
      {
        id: 'amazon-main',
        label: '亚马逊主图',
        width: 2000,
        height: 2000,
        note: '1:1，满足纯白底最小 1000px 要求',
      },
      { id: 'pdd-main', label: '拼多多主图', width: 750, height: 750, note: '1:1' },
      { id: 'shopify-product', label: 'Shopify 商品图', width: 2048, height: 2048, note: '1:1' },
    ],
  },
  {
    id: 'app',
    label: 'App',
    templates: [
      {
        id: 'ios-6.7',
        label: 'App Store 截图（6.7" iPhone）',
        width: 1290,
        height: 2796,
        note: '竖屏',
      },
      {
        id: 'ios-6.5',
        label: 'App Store 截图（6.5" iPhone）',
        width: 1284,
        height: 2778,
        note: '竖屏',
      },
      { id: 'android-phone', label: 'Google Play 手机截图', width: 1080, height: 1920, note: '9:16' },
      {
        id: 'app-icon',
        label: 'App 图标（iOS/Android 通用源图）',
        width: 1024,
        height: 1024,
        note: '1:1',
      },
    ],
  },
  {
    id: 'website',
    label: '网站',
    templates: [
      { id: 'hero-banner', label: '网页首屏 Banner', width: 1920, height: 1080, note: '16:9' },
      { id: 'og-image', label: 'Open Graph 分享图', width: 1200, height: 630, note: '1.91:1' },
      { id: 'favicon-src', label: 'Favicon 源图', width: 512, height: 512, note: '1:1，导出时再缩' },
      { id: 'email-header', label: '邮件 Header 横幅', width: 600, height: 200, note: '邮件客户端安全宽度' },
    ],
  },
] as const;
